import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '$store';
import {
  addRelation,
  checkAssetDuplicates,
  commitBinaryUpdate,
  commitUpload,
  getContributionAssetDraft,
  getPublishedContributionAsset,
  getUploadTicket,
  postAssetData,
  removeAsset,
  saveContributionAsset,
  sendFile,
  sendFileWithTus,
} from '~modules/media-library/controller/api/assets';
import { AssetId } from '~modules/media-library/model/Asset';
import { StatusActions } from '~store/actions';

export const selectUploadAssetsState = (state: RootState) => state.contribute;

interface commitBinaryResponseType {
  assetId?: string;
  version?: number;
}

interface saveContributionAssetType {
  message?: string;
  status?: boolean;
  errors?: {};
}

// Save Contribution Draft
export const saveContributionDraft = createAsyncThunk<
  { requestPayload: any; requestResponse: saveContributionAssetType },
  { data: any; assetId: string; contributionType: string },
  { dispatch: any; rejectValue: any }
>('assets/saveContributionDraft', async ({ data, assetId, contributionType }, { dispatch, rejectWithValue }) => {
  try {
    const res: saveContributionAssetType = await saveContributionAsset(data, assetId, contributionType);

    if (res.status === true) {
      dispatch(setContribution({ message: res.message, status: res.status, loading: false }));
      // Fetch the updated contribution asset
      dispatch(fetchContributionAsset({ assetId, contributionType }));
    }

    return {
      requestPayload: data,
      requestResponse: res,
    };
  } catch (e) {
    return rejectWithValue(e);
  }
});

// Save Contribution Draft
export const saveAssetData = createAsyncThunk<
  saveContributionAssetType,
  { data: any; assetId: string; contributionType: string },
  { dispatch: any }
>('assets/saveAssetData', async ({ data, assetId, contributionType }, { dispatch }) => {
  const res: saveContributionAssetType = await postAssetData(data, assetId);

  if (res.status === true) {
    // save response on the state
    dispatch(setContribution({ message: res.message, status: res.status, loading: false }));

    // Fetch the updated contribution asset
    dispatch(fetchContributionAsset({ assetId, contributionType }));
  }

  return res;
});

interface FetchContributionAssetProps {
  assetId: string;
  contributionType?: string;
  workflowStatus?: string;
}
// fetch contribution asset
export const fetchContributionAsset = createAsyncThunk(
  'assets/getContributionAsset',
  async (data: FetchContributionAssetProps, { rejectWithValue }) => {
    try {
      const response =
        (await data.workflowStatus) === 'published'
          ? getPublishedContributionAsset(data.assetId)
          : getContributionAssetDraft(data.assetId, data.contributionType);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// multi file upload
export const upload = createAsyncThunk(
  'upload/uploadFile',
  async ({ files }: { files: { file: File; hash: string }[] }, { dispatch, getState }) => {
    const state = getState() as RootState;

    // Reset the uploads completed flag before starting
    dispatch(setUploadsCompleted(false));
    dispatch(setProcessInProgress(true));

    try {
      const { contributionMode, currentAssetId } = state.contribute;

      // Iterate over each file and upload it
      for (const [index, { file, hash }] of files.entries()) {
        dispatch(setFile(file));

        // get duplicates using the corresponding hash
        const assetHasDuplicates = await checkAssetDuplicates(hash);

        if (assetHasDuplicates) {
          dispatch(
            StatusActions.setErrorNotification(
              `File ${file.name} already exists in the system. Please upload a different file.`,
            ),
          );
          continue;
        } else {
          dispatch(
            addUploadedFile({
              fileName: file.name,
              fileSize: file.size,
            }),
          );
        }

        dispatch(setUploadInProgress(true));

        // request upload ticket
        let ticketRes;
        try {
          ticketRes = (await getUploadTicket()) as { key: string; url: string; tus_capable: boolean };
        } catch (e: any) {
          const errorMessage = e.error || (e instanceof Error ? e.message : 'Unknown error');
          dispatch(
            updateUploadedFile({
              fileName: file.name,
              error: true,
              errorMessage,
            }),
          );
        }

        // upload binary
        try {
          if (ticketRes.tus_capable) {
            // Use TUS protocol for resumable uploads if supported by the server
            await sendFileWithTus(ticketRes.key, file, ticketRes?.url, progress => {
              dispatch(updateFileProgress({ fileName: file.name, progress, index }));
            });
          } else {
            // Fall back to regular file upload if TUS is not supported
            await sendFile(file, ticketRes?.url, progress => {
              dispatch(updateFileProgress({ fileName: file.name, progress, index }));
            });
          }
        } catch (e: any) {
          const errorMessage = e.error || (e instanceof Error ? e.message : 'Unknown error');
          dispatch(
            updateUploadedFile({
              fileName: file.name,
              error: true,
              errorMessage,
            }),
          );
        }

        // commit upload
        const fileName = file.name;

        // replace binary of an existing asset
        if (contributionMode === 'replace') {
          try {
            const commitBinaryResponse: commitBinaryResponseType = await commitBinaryUpdate(
              ticketRes?.key,
              fileName,
              currentAssetId,
            );
            if (commitBinaryResponse.assetId) {
              dispatch(setContribution({ message: 'File is replaced successfully!', loading: false }));
              dispatch(setNextStep());
            }
          } catch (e: any) {
            const errorMessage = e.error || (e instanceof Error ? e.message : 'Unknown error');
            dispatch(
              updateUploadedFile({
                fileName: file.name,
                error: true,
                errorMessage: errorMessage,
              }),
            );
          }

          // Set uploads completed flag and return
          dispatch(setUploadInProgress(false));
          dispatch(setUploadsCompleted(true));
          return;
        }

        // commit new uploaded asset
        let commitUploadResponse;
        try {
          commitUploadResponse = await commitUpload(ticketRes?.key, fileName);
        } catch (e: any) {
          const errorMessage = e.error || (e instanceof Error ? e.message : 'Unknown error');
          dispatch(setUploadInProgress(false));

          dispatch(
            updateUploadedFile({
              fileName: file.name,
              error: true,
              errorMessage: errorMessage,
            }),
          );
        }

        dispatch(
          updateUploadedFile({
            fileName,
            contributionType: commitUploadResponse.contributionType,
            id: commitUploadResponse.id,
            uploadTicket: ticketRes?.key,
          }),
        );

        dispatch(toggleSelectedAssetId(commitUploadResponse.id.toString()));

        //  if file is uploaded successfully setUploadInProgress to false
        if (commitUploadResponse.id) {
          dispatch(setUploadInProgress(false));
          dispatch(setProcessCanStart(true));
        }
      }
    } catch (e) {
      dispatch(setProcessInProgress(false));
      console.error('Error during file upload:', e);
      throw e;
    }

    // Set uploads completed flag after all files are processed
    dispatch(setUploadsCompleted(true));
  },
);

// removeAsset
export const removeAssetThunk = createAsyncThunk(
  'upload/removeAsset',
  async (assetId: AssetId, { getState, dispatch }) => {
    const state = getState() as RootState;

    const uploadAssetsState = selectUploadAssetsState(state);

    try {
      dispatch(removeUploadedFile(assetId));
      const commitUploadResponse = await removeAsset(assetId, uploadAssetsState.upload_ticket);
      return commitUploadResponse;
    } catch (e) {
      return e;
    }
  },
);

// add relation
export const addRelationThunk = createAsyncThunk(
  'relation',
  async ({ files, assetId }: { files: any[]; assetId: string }) => {
    try {
      const relationPromises = files.map(file =>
        addRelation(file.relationType.toLowerCase(), assetId, file.relatedAssetId),
      );

      const responses = await Promise.all(relationPromises);
      return responses;
    } catch (e) {
      return e;
    }
  },
);
// multi-asset editing
export const processNextAsset = createAsyncThunk('assets/processNextAsset', async (_, { getState, dispatch }) => {
  const state = getState() as RootState;
  const { currentAssetIndex, selectedAssetIds, contributionMode } = state.contribute;

  if (currentAssetIndex === -1 && selectedAssetIds.length > 0) {
    dispatch(setCurrentAssetId(selectedAssetIds[0]));
    dispatch(incrementCurrentAssetIndex());
    return;
  }

  if (currentAssetIndex < selectedAssetIds.length - 1) {
    dispatch(incrementCurrentAssetIndex());
    dispatch(setCurrentAssetId(selectedAssetIds[currentAssetIndex + 1]));
    // if contribution mode is new, start from the second step since the first step is upload
    if (contributionMode === 'new') {
      dispatch(setCurrentStep(1));
    } else {
      dispatch(setCurrentStep(0));
    }
  }
});

export const toggleSelectedFieldThunk = createAsyncThunk(
  'contribute/toggleSelectedField',
  async (fieldName: string, { getState, dispatch }) => {
    const state = getState() as RootState;
    const contributeState = state.contribute;
    const consentsState = state.assetRelations;

    if (fieldName in contributeState.selectedFields) {
      const { [fieldName]: _, ...rest } = contributeState.selectedFields;
      dispatch(setFieldValue({ fieldName, value: rest }));
    } else {
      let selectedFieldValue = contributeState.contributionDraftData[fieldName];

      if (fieldName === 'relevantDocuments') {
        selectedFieldValue = consentsState.relevantDocuments;
      }

      dispatch(
        setFieldValue({
          fieldName,
          value: {
            ...contributeState.selectedFields,
            [fieldName]: selectedFieldValue,
          },
        }),
      );
    }

    return { fieldName };
  },
);

const resetStateFields = (state: any) => {
  state.isProcessInProgress = false;
  state.confirmCancelModalIsOpen = false;
  state.contributionMode = null;
  state.currentAssetId = ' ';
  state.currentStep = 0;
  state.selectedFields = {};
  state.selectedAssetIds = [];
  state.allFiles = [];
  state.processCanStart = false;
  state.currentAssetIndex = 0;
  state.uploadsCompleted = false;
};

export interface ContributeStateType {
  uploadInProgress: boolean;
  contributionMode: string;
  selectedAssetIds: AssetId[];
  selectedFields: Record<string, any>;
  isProcessInProgress: boolean;
  uploadModal: boolean;
  editingMultiAsset: boolean;
  contributionSavedLoading: boolean;
  contribution: {};
  processCanStart: boolean;
  currentStep: number;
  contributionType: string;
  contributionDataLoading: boolean;
  contributionDraftSchema: {};
  contributionDraftData: {};
  onSaveContributionDraftResponse: {};
  allFiles: ContributeFile[];
  upload_ticket: string;
  upload_url: string;
  asset_id: AssetId;
  file: File;
  message: string;
  error: string;
  progress: number;
  test: string;
  fileSelected: { contributionType: string; fileName: string };
  currentAssetId: string;
  currentAssetIndex: number;
  confirmCancelModalIsOpen: boolean;
  uploadsCompleted: boolean;
}
export const initialState: ContributeStateType = {
  uploadInProgress: false,
  contributionMode: '',
  selectedAssetIds: [],
  selectedFields: {},
  isProcessInProgress: false,
  uploadModal: false,
  editingMultiAsset: false,
  contributionSavedLoading: false,
  contribution: { status: false, message: '', loading: false, type: '' },
  processCanStart: false,
  currentStep: 0,
  contributionType: '',
  contributionDataLoading: true,
  contributionDraftSchema: {},
  contributionDraftData: {},
  onSaveContributionDraftResponse: {},
  allFiles: [],
  upload_ticket: '',
  upload_url: '',
  asset_id: null,
  file: null,
  message: '',
  error: '',
  progress: 0,
  test: '',
  fileSelected: { contributionType: '', fileName: '' } as { contributionType: string; fileName: string },
  currentAssetId: '',
  currentAssetIndex: 0,
  confirmCancelModalIsOpen: false,
  uploadsCompleted: false, // New flag to track when uploads are complete
};

const ContributeSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    // Add new reducer for setting uploadsCompleted flag
    setUploadsCompleted: (state, action) => {
      state.uploadsCompleted = action.payload;
    },
    removeRelevantDocument: (state, action: PayloadAction<AssetId>) => {
      const relatedAssetIdToRemove = action.payload;
      if (state.selectedFields.relevantDocuments) {
        state.selectedFields.relevantDocuments = state.selectedFields.relevantDocuments.filter(
          document => document.relatedAssetId !== relatedAssetIdToRemove,
        );
      }
    },
    removeField: (state, action: PayloadAction<string>) => {
      const fieldName = action.payload;
      const { [fieldName]: _, ...rest } = state.selectedFields;
      state.selectedFields = rest;
    },
    toggleSelectedAssetId: (state, action: PayloadAction<AssetId>) => {
      const assetId = action.payload;
      if (state.selectedAssetIds.length === 0) {
        state.currentAssetId = assetId;
      }
      const index = state.selectedAssetIds.indexOf(assetId);
      if (index === -1) {
        state.selectedAssetIds.push(assetId);
      } else {
        state.selectedAssetIds.splice(index, 1);
      }
    },
    onConfirmCancelModal: state => {
      resetStateFields(state);
    },
    setFieldValue: (state, action) => {
      const { value } = action.payload;
      state.selectedFields = value;
    },
    onCloseConfirmCancelModal: state => {
      state.confirmCancelModalIsOpen = false;
    },
    onCloseContributionWizard: state => {
      if (state.isProcessInProgress && !state.uploadInProgress) {
        state.confirmCancelModalIsOpen = true;
        return;
      }
      if (!state.isProcessInProgress) {
        resetStateFields(state);
      }
    },
    onOpenContributionWizard: (state, action) => {
      const { id, contributionMode } = action.payload;
      state.contributionDraftData = {};
      state.currentAssetId = id;
      state.contributionMode = contributionMode;
      state.uploadsCompleted = false;
    },
    onEdiMultiAssets: state => {
      state.currentAssetId = state.selectedAssetIds[0];
      state.contributionMode = 'edit-multi';
    },
    setUploadInProgress: (state, action: PayloadAction<boolean>) => {
      state.uploadInProgress = action.payload;
    },
    setProcessInProgress: (state, action: PayloadAction<boolean>) => {
      state.isProcessInProgress = action.payload;
    },
    setCurrentAssetId: (state, action: PayloadAction<AssetId>) => {
      state.currentAssetId = action.payload;
    },
    incrementCurrentAssetIndex: state => {
      state.currentAssetIndex += 1;
    },
    resetAllFiles: state => {
      state.allFiles = [];
      state.uploadsCompleted = false;
    },
    resetSelectedFields: state => {
      state.selectedFields = {};
    },
    resetSelectedAssets: state => {
      state.selectedAssetIds = [];
    },
    setContribution: (state, action) => {
      const currentContribution = { ...state.contribution };
      state.contribution = { ...currentContribution, ...action.payload };
    },
    updateFileContributionType: (state, action: PayloadAction<string>) => {
      state.fileSelected = { contributionType: action.payload, fileName: '' };
    },
    updateFileProgress: (state, action) => {
      const { fileName, progress } = action.payload;
      const file = state.allFiles.find(file => file.fileName === fileName);
      if (file) {
        file.progress = progress;
      }
    },
    updateUploadedFile: (state, action: PayloadAction<ContributeFile>) => {
      const { fileName, contributionType, id, errorMessage } = action.payload;
      const file = state.allFiles.find(file => file.fileName === fileName);
      if (file) {
        file.contributionType = contributionType;
        file.id = id;
        file.errorMessage = errorMessage;
      }
    },
    removeUploadedFile: (state, action: PayloadAction<AssetId>) => {
      const assetIdToRemove = action.payload;
      state.allFiles = current(state.allFiles).filter(file => file.id !== assetIdToRemove);
    },
    addUploadedFile: (state, action: PayloadAction<ContributeFile>) => {
      state.allFiles.push(action.payload);
    },
    changeContributionMode: (state, action: PayloadAction<string>) => {
      // if contribution mode is new reset selectedAssetIds
      if (action.payload === 'new') {
        state.selectedAssetIds = [];
        state.contributionDraftData = {};
      }
      // reset all related state
      state.currentStep = 0;
      state.currentAssetIndex = 0;
      state.fileSelected = { contributionType: '', fileName: '' };
      state.allFiles = [];
      state.processCanStart = false;
      state.contributionMode = action.payload;
      state.uploadsCompleted = false; // Reset uploads completed flag
    },
    toggleSelectedField: (state, action: PayloadAction<string>) => {
      const fieldName = action.payload;

      if (fieldName in state.selectedFields) {
        const { [fieldName]: _, ...rest } = state.selectedFields;
        state.selectedFields = rest;
      } else {
        state.selectedFields[fieldName] = state.contributionDraftData[fieldName];
      }
    },
    setMultiAssetModalOpen: (state, action: PayloadAction<boolean>) => {
      state.editingMultiAsset = action.payload;
      state.currentStep = 0;
    },
    setUploadModalOpen: (state, action: PayloadAction<boolean>) => {
      state.uploadModal = action.payload;
      state.contributionSavedLoading = false;
      state.contributionDataLoading = true;
      state.processCanStart = false;
    },
    setProcessCanStart: (state, action: PayloadAction<boolean>) => {
      state.processCanStart = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
      if (action.payload === 1) {
        state.isProcessInProgress = true;
      }
    },
    setNextStep: state => {
      state.currentStep = state.currentStep + 1;
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    setFile: (state, action: PayloadAction<File>) => {
      state.file = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchContributionAsset.pending, state => {
      state.contributionDataLoading = true;
    });
    builder.addCase(fetchContributionAsset.fulfilled, (state, action: any) => {
      const { schema, data } = action.payload;

      state.contributionDraftSchema = schema;

      // format dates before setting the state
      if (data) {
        Object.keys(data).forEach(fieldName => {
          if (fieldName === 'LicenceStartDate' || fieldName === 'LicenceExpirationDate') {
            const dateValue = data[fieldName]?.date;
            data[fieldName] = dateValue ? dateValue.split(' ')[0] : '';
          }
        });
      }

      state.contributionDraftData = data;

      state.contributionDataLoading = false;
    });
    builder.addCase(saveContributionDraft.pending, (state: any) => {
      state.contributionSavedLoading = true;
    });

    builder.addCase(saveContributionDraft.fulfilled, (state, action: any) => {
      const { requestResponse } = action.payload;
      const { currentAssetId, selectedAssetIds, contributionMode } = state;
      if (contributionMode !== 'edit-single' && currentAssetId === selectedAssetIds[selectedAssetIds.length - 1]) {
        state.isProcessInProgress = false;
      }

      if (contributionMode === 'edit-single') {
        state.isProcessInProgress = false;
      }
      state.onSaveContributionDraftResponse = requestResponse;
    });
    builder.addCase(saveAssetData.pending, (state: any) => {
      state.contributionSavedLoading = true;
    });
    builder.addCase(saveAssetData.fulfilled, (state, action: any) => {
      state.isProcessInProgress = false;
      state.onSaveContributionDraftResponse = action.payload;
    });
  },
});

export const {
  setFieldValue,
  setUploadInProgress,
  onConfirmCancelModal,
  onCloseConfirmCancelModal,
  resetSelectedAssets,
  onEdiMultiAssets,
  resetSelectedFields,
  toggleSelectedField,
  removeRelevantDocument,
  setMultiAssetModalOpen,
  toggleSelectedAssetId,
  setProcessInProgress,
  incrementCurrentAssetIndex,
  setCurrentAssetId,
  setContribution,
  updateFileContributionType,
  updateFileProgress,
  updateUploadedFile,
  setProgress,
  setFile,
  setCurrentStep,
  setNextStep,
  setProcessCanStart,
  setUploadModalOpen,
  addUploadedFile,
  resetAllFiles,
  onCloseContributionWizard,
  onOpenContributionWizard,
  removeUploadedFile,
  setUploadsCompleted,
} = ContributeSlice.actions;

export default ContributeSlice.reducer;
